// -----------------------------------------------------------------------------
// launchedEditor.js — 2025-05-08  (mobile-first refactor v3)
// • Airier spacing, modern card shadow, glass-like hover
// • “Approve” hidden once a live link exists
// • Uses campaigns.sendFeedback helper
// FULL component – copy-paste ready
// -----------------------------------------------------------------------------

import React, { useEffect, useMemo, useState } from "react";
import { Avatar, Box, Button, Chip, Divider, IconButton, List, ListItem, ListItemAvatar, ListItemText, Snackbar, Tabs, Tab, Tooltip, Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField, useMediaQuery,  } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { styled } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom"; // Removed useSearchParams
import DoneAllIcon from "@mui/icons-material/DoneAll";
import PeopleIcon from "@mui/icons-material/People";
import LinkIcon from "@mui/icons-material/Link";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FeedbackIcon from "@mui/icons-material/Feedback";
import TikTokIcon from "@mui/icons-material/MusicNote"; // TikTok icon (placeholder)
import InstagramIcon from "@mui/icons-material/CameraAlt"; // Instagram icon
import YouTubeIcon from "@mui/icons-material/YouTube"; // YouTube icon
import PersonIcon from "@mui/icons-material/Person"; // Generic icon for other platforms

import {
  getFollowersByVideos,
  getTotalFollowersInDict,
} from "../../../../../Utils/constants";
import client from "../../../../../API";

/* ───────── helpers ───────── */
const formatNumber = (n) =>
  Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

// ────────── Platform Icon Helper ──────────
const getPlatformIcon = (platform) => {
  switch (platform?.toLowerCase()) {
    case "tiktok":
      return <TikTokIcon fontSize="small" />;
    case "instagram":
      return <InstagramIcon fontSize="small" />;
    case "youtube":
      return <YouTubeIcon fontSize="small" />;
    default:
      return <PersonIcon fontSize="small" />;
  }
};

// ────────── Platform Name Helper ──────────
const getPlatformName = (platform) => {
  return platform || "Unknown Platform";
};

const RowWrapper = styled(Box)(({ theme, $highlight }) => ({
  borderRadius: 16,
  position: "relative",
  overflow: "hidden",
  background: theme.palette.background.paper,
  border: `2px solid ${$highlight ? theme.palette.primary.main : "transparent"}`,
  boxShadow: "0 6px 20px rgba(0,0,0,0.06)",
  transition: "all .3s ease",
  marginBottom: theme.spacing(3),
  "&:hover": {
    boxShadow: "0 10px 32px rgba(0,0,0,0.08)",
    transform: "translateY(-2px)",
  },
  "::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background:
      "linear-gradient(135deg, rgba(255,255,255,.08) 0%, rgba(255,255,255,0) 100%)",
    pointerEvents: "none",
  },
}));

/* ───────── Row component ───────── */
function LaunchCreatorRow({
  creator,
  campaignId,
  highlight,
  onApproveAssets,
  isMutating,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const liveLinksFilled = creator?.videos?.every((v) => v.liveLink);

  /* feedback dialog */
  const [openFb, setOpenFb] = useState(false);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const handleSendFeedback = async () => {
    try {
      setSending(true);
      await client.campaigns.sendFeedback(campaignId, {
        creatorId: creator.id,
        message,
      });
      setOpenFb(false);
      setMessage("");
    } finally {
      setSending(false);
    }
  };

  const handleApproveClick = () => {
    if (
      isMutating ||
      !creator.assetsSent ||
      creator.assetsApproved ||
      liveLinksFilled
    )
      return;
    onApproveAssets(campaignId, creator.id, true);
  };

  return (
    <>
      <RowWrapper $highlight={highlight} id={`creator-${creator.id}`}>
        <ListItem
          disableGutters
          sx={{
            gap: 2,
            py: isMobile ? 2 : 3,
            px: isMobile ? 2 : 4,
            flexDirection: isMobile ? "column" : "row",
            alignItems: "flex-start",
          }}
        >
          {/* Avatar */}
          <ListItemAvatar sx={{ mr: isMobile ? 0 : 2 }}>
            <Avatar
              src={creator.pfphref || ""}
              alt={creator.name}
              sx={{ width: 56, height: 56 }}
            />
          </ListItemAvatar>

          {/* Creator identity */}
          <ListItemText
            sx={{ width: "100%", mb: isMobile ? 1.5 : 0 }}
            primary={
              <Box
                display="flex"
                alignItems="center"
                flexWrap="wrap"
                gap={1}
              >
                <Typography
                  component="a" // Changed to <a> for external links
                  href={creator.platformLink || "#"} // Fallback to "#" if platformLink is missing
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontWeight: 700,
                    color: "text.primary",
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                    fontSize: isMobile ? "1rem" : "1.05rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {getPlatformIcon(creator.promotionPlatform)}
                  @{creator.name}
                </Typography>

                <Chip
                  size="small"
                  label={
                    creator.assetsApproved
                      ? "Assets Approved"
                      : creator.assetsSent
                      ? "Pending Approval"
                      : "Not Submitted"
                  }
                  color={
                    creator.assetsApproved
                      ? "success"
                      : creator.assetsSent
                      ? "warning"
                      : "default"
                  }
                  icon={<DoneAllIcon fontSize="inherit" />}
                />

                {liveLinksFilled && (
                  <Chip
                    size="small"
                    label="Live Link(s)"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>
            }
            secondary={
              <Box
                display="flex"
                alignItems="center"
                flexWrap="wrap"
                gap={1}
                mt={0.5}
              >
                <PeopleIcon
                  sx={{ color: "text.secondary", fontSize: 18 }}
                />
                <Typography variant="body2" color="text.secondary">
                  Followers:&nbsp;
                  {formatNumber(
                    getTotalFollowersInDict(
                      getFollowersByVideos(creator.videos)
                    )
                  )}
                </Typography>
              </Box>
            }
          />

          {/* Actions / Links */}
          <Box
            sx={{
              ml: "auto",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
              width: isMobile ? "100%" : "auto",
              justifyContent: isMobile ? "flex-start" : "flex-end",
            }}
          >
            {creator.submissionLink && (
              <Tooltip title="Open submitted assets">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<LinkIcon />}
                  onClick={() => window.open(creator.submissionLink, "_blank")}
                >
                  Assets
                </Button>
              </Tooltip>
            )}

            {creator.videos.map(
              (v) =>
                v.liveLink && (
                  <Tooltip key={v.id} title="Open live link">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<LinkIcon />}
                      onClick={() => window.open(v.liveLink, "_blank")}
                    >
                      Video
                    </Button>
                  </Tooltip>
                )
            )}

            {!liveLinksFilled && (
              <Button
                size="small"
                variant="contained"
                disabled={
                  !creator.assetsSent || creator.assetsApproved || isMutating
                }
                onClick={handleApproveClick}
              >
                Approve
              </Button>
            )}

            <IconButton
              size="small"
              onClick={() => setOpenFb(true)}
              color="primary"
            >
              <FeedbackIcon />
            </IconButton>
          </Box>
        </ListItem>
      </RowWrapper>

      {/* Feedback dialog */}
      <Dialog open={openFb} onClose={() => setOpenFb(false)} fullWidth>
        <DialogTitle>Feedback for @{creator.name}</DialogTitle>
        <DialogContent>
          <TextField
            multiline
            minRows={4}
            fullWidth
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Your notes to the creator…"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenFb(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSendFeedback}
            disabled={sending || !message.trim()}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/* ───────── Main panel ───────── */
export default function CampaignDetailsPanelLaunched({
  campaignDetails,
  onApproveAssets,
  isMutatingAssets = false,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [snack, setSnack] = useState(null);

  /* tab ↔ filter mapping */
  const filterToIndex = { all: 0, live: 1, pending: 2, not: 3 };
  const indexToFilter = ["all", "live", "pending", "not"];
  const [activeTab, setActiveTab] = useState(0);

  const handleChangeTab = (_, v) => {
    setActiveTab(v);
  };

  /* sort & filter */
  const sortedCreators = useMemo(
    () => [...campaignDetails.creators].sort((a, b) => a.name.localeCompare(b.name)),
    [campaignDetails.creators]
  );

  const filteredCreators = useMemo(() => {
    switch (activeTab) {
      case 1:
        return sortedCreators.filter((c) => c.videos.every((v) => v.liveLink));
      case 2:
        return sortedCreators.filter((c) => c.assetsSent && !c.assetsApproved);
      case 3:
        return sortedCreators.filter((c) => !c.assetsSent);
      default:
        return sortedCreators;
    }
  }, [sortedCreators, activeTab]);

  /* clipboard helpers */
  const copyToClipboard = async (txt) => {
    await navigator.clipboard.writeText(txt);
    setSnack("Copied to clipboard!");
  };

  const handleExportVideos = () => {
    const lines = sortedCreators
      .filter((c) => c.videos.every((v) => v.liveLink))
      .flatMap((c) => c.videos.map((v) => `@${c.name}: ${v.liveLink}`))
      .join("\n");
    copyToClipboard(lines);
  };

  const handleExportDrafts = () => {
    const lines = sortedCreators
      .filter((c) => c.assetsSent && c.submissionLink)
      .map((c) => `@${c.name}: ${c.submissionLink}`)
      .join("\n");
    copyToClipboard(lines);
  };

  return (
    <Box>
      {/* Export buttons */}
      <Box
        sx={{
          p: 2,
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          justifyContent: "flex-end",
        }}
      >
        <Button
          variant="outlined"
          startIcon={<ContentCopyIcon />}
          onClick={handleExportVideos}
        >
          Export Video Links
        </Button>
        <Button
          variant="outlined"
          startIcon={<ContentCopyIcon />}
          onClick={handleExportDrafts}
        >
          Export Draft Links
        </Button>
      </Box>

      {/* Filter tabs */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          bgcolor: "background.paper",
          zIndex: theme.zIndex.appBar,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleChangeTab}
          variant="scrollable"
          scrollButtons={isMobile ? "auto" : false}
          aria-label="Creator filters"
          sx={{
            "& .MuiTab-root": { py: 1.5, minWidth: 100, fontWeight: 600 },
            "& .MuiTabs-indicator": { height: 3 },
          }}
        >
          <Tab label={`All (${sortedCreators.length})`} />
          <Tab label="Live Links" />
          <Tab label="Pending" />
          <Tab label="Not Submitted" />
        </Tabs>
      </Box>

      {/* Creator rows */}
      {filteredCreators.length === 0 ? (
        <Box p={4} textAlign="center" color="text.secondary">
          (No creators in this view)
        </Box>
      ) : (
        <List disablePadding sx={{ p: isMobile ? 1 : 2 }}>
          {filteredCreators.map((creator) => (
            <LaunchCreatorRow
              key={creator.id}
              creator={creator}
              campaignId={campaignDetails.id}
              highlight={false} // Removed highlight logic
              onApproveAssets={onApproveAssets}
              isMutating={isMutatingAssets}
            />
          ))}
        </List>
      )}

      {/* Snackbar */}
      <Snackbar
        open={!!snack}
        autoHideDuration={2500}
        onClose={() => setSnack(null)}
        message={snack}
      />
    </Box>
  );
}